import type { RewardedAdProvider } from "@yjsgames/ad-ports";

const DEFAULT_REWARDED_AD_TIMEOUT_MS = 120_000;

export interface RewardedLoadingModals {
  /** 광고 Provider 호출 전 사용자에게 보여줄 로딩 모달을 연다. */
  openAdLoading(): void;
  /** 광고 완료, 스킵, 예외 발생 시 로딩 모달을 닫는다. */
  closeAdLoading(): void;
}

export interface RewardedLoadingOptions {
  readonly isProcessing?: () => boolean;
  readonly setProcessing?: (processing: boolean) => void;
  readonly timeoutMs?: number;
}

/**
 * 로딩 모달을 먼저 띄운 뒤 다음 paint에서 보상형 광고 Provider를 호출한다.
 * Provider가 동기 예외를 던져도 모달을 닫고 예외를 그대로 재전파한다.
 */
export function showRewardedWithLoading(
  rewardedAd: RewardedAdProvider,
  modals: RewardedLoadingModals,
  onReward: () => void,
  options: RewardedLoadingOptions = {},
): void {
  if (options.isProcessing?.()) {
    modals.openAdLoading();
    return;
  }
  let settled = false;
  let timeout: ReturnType<typeof setTimeout> | null = null;
  const settle = (rewarded: boolean): void => {
    if (settled) return;
    settled = true;
    if (timeout !== null) {
      clearTimeout(timeout);
      timeout = null;
    }
    modals.closeAdLoading();
    options.setProcessing?.(false);
    if (rewarded) onReward();
  };
  modals.openAdLoading();
  options.setProcessing?.(true);
  timeout = setTimeout(() => settle(false), options.timeoutMs ?? DEFAULT_REWARDED_AD_TIMEOUT_MS);
  runAfterNextPaint(() => {
    if (settled) return;
    try {
      rewardedAd.showRewarded({
        onReward: () => settle(true),
        onSkip: () => settle(false),
      });
    } catch (error) {
      settle(false);
      throw error;
    }
  });
}

/** 브라우저 paint 1회를 양보하고, RAF가 없는 테스트/비브라우저 환경은 타이머로 대체한다. */
export function runAfterNextPaint(cb: () => void): void {
  if (typeof requestAnimationFrame === "function") {
    requestAnimationFrame(() => cb());
    return;
  }
  setTimeout(cb, 0);
}
